import React, { useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
//import FactCheckIcon from '@mui/icons-material/FactCheck';

import { PaginationParams } from 'api/bitedb/products/products';
import { useGetProductsByProductId } from 'api/bitedb/products/products.hooks';

import FormattedName from './ProductNameColumn';

interface ISingleResultRow {
  keyVal?: number;
  isOpen?: boolean;
  onToggle?: () => void;
  transaction?: any;
  location?: string;
  selectedItems?: any;
  setSelectedItems?: any;
  handleClassify?: any;
  handleAdd?: any;
  handleEdit?: any;
  handleDelete?: any;
  viewType?: string;
  aiInProgress?: boolean;
}

const ProductPageRow: React.FC<ISingleResultRow> = ({
  transaction,
  location,
  selectedItems,
  setSelectedItems,
  handleClassify,
  handleAdd,
  handleEdit,
  handleDelete,
  viewType,
  aiInProgress,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const paginationParams: PaginationParams = {
    page: 1,
    limit: 250,
    sortBy: 'name',
    sortOrder: 'asc',
  };

  const { data: productsData, refetch: refetchProducts } =
    useGetProductsByProductId(transaction?.id ?? '-1', '', paginationParams);

  const handleOpen = () => {
    refetchProducts();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleCheckboxClick = (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];

      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);

      return alreadySelected
        ? safePrevSelected.filter((i) => i.id !== item.id)
        : [...safePrevSelected, item];
    });
  };

  const formatHTSCode = (code: string | null | undefined): string => {
    if (!code) {
      return 'No Code';
    }

    // 1. Remove all non-digit characters (dots, spaces, dashes, etc.)
    const cleanCode = code.toString().replace(/\D/g, '');

    // 2. Check if we have the standard 10-digit HTS code
    if (cleanCode.length === 10) {
      // Format: xxxx.xx.xxxx
      return `${cleanCode.slice(0, 4)}.${cleanCode.slice(
        4,
        6,
      )}.${cleanCode.slice(6)}`;
    }

    // 3. Fallback: If it's not 10 digits (e.g. 8 digits or 6 digits), return as is or apply partial formatting
    // Returning original 'clean' version or the original input is safer than forcing a format that doesn't fit.
    return code;
  };

  const OpenIcon = () => {
    if (transaction?.child_products > 0) {
      return isOpen ? (
        <div onClick={() => handleClose()}>
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M18 9.5L12 15.5L6 9.5'
              stroke='var(--action-color)'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      ) : (
        <div onClick={() => handleOpen()}>
          <svg
            width='8'
            height='14'
            viewBox='0 0 8 14'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M1 1L7 7L1 13'
              stroke='var(--noaction-color)'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      );
    }
    return <div style={{ width: '24', height: '24' }}></div>;
  };

  const ChildTable = () => {
    return isOpen ? (
      <div
        style={{
          borderLeft: '1px dotted var(--action-color)',
          width: '100%',
          paddingLeft: '12px',
        }}
      >
        {productsData &&
          productsData.map((r) => {
            return (
              <ProductPageRow
                key={r.id}
                location={`/product/${r.id}`}
                viewType='Product View'
                transaction={r}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleClassify={handleClassify}
                handleAdd={handleAdd}
              />
            );
          })}
      </div>
    ) : (
      <></>
    );
  };

  const additionalDuties = () => {
    const rates = [0.0];
    if (
      transaction?.tariff_result?.results?.lines &&
      transaction?.tariff_result?.results?.lines.length > 0
    ) {
      transaction?.tariff_result?.results?.lines.forEach((line: any) => {
        if (line.rows) {
          line.rows.forEach((row: any) => {
            if (row.rate.includes('%')) {
              rates.push(Number.parseFloat(row.rate.replace('%', '')));
            }
          });
        }
      });
    }
    return `${rates
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
      .toFixed(2)}%`;
  };

  const hasInvalidHTS =
    transaction?.tariff_result?.status?.message === 'Invalid Country of Origin';

  return (
    <>
      <div className='screening-results-row'>
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: '48px' }}
        >
          <div
            className='screening-results-row-default-cell-inner'
            style={{ width: '48px' }}
            onClick={() => handleCheckboxClick(transaction ? transaction : [])}
          >
            {selectedItems?.some(
              (item: { id: void }) => item.id === transaction?.id,
            ) ? (
              <div className='full-list-filters-cont-row-icon-item'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='12'
                  height='12'
                  viewBox='0 0 12 12'
                  fill='none'
                >
                  <path
                    d='M10 3L4.5 8.5L2 6'
                    stroke='var(--text-primary)'
                    strokeWidth='1.6666'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            ) : (
              <div className='full-list-filters-cont-row-icon-item-unselected'></div>
            )}
          </div>
        </div>
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: '44px' }}
        >
          <div
            className='screening-results-row-default-cell-inner'
            style={{ width: '44px' }}
          >
            <OpenIcon />
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <FormattedName
              transaction={transaction}
              viewType={viewType || 'Product View'}
              location={location || `/product/${transaction.id}`}
              handleEdit={handleEdit}
            />
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div
            className='screening-results-row-default-cell-inner'
            title={hasInvalidHTS ? 'Invalid HTS/Country Combination' : ''}
          >
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.hts_code &&
                transaction?.hts_code !== '' &&
                !hasInvalidHTS
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {formatHTSCode(transaction?.hts_code)}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div
            className='screening-results-row-default-cell-inner'
            title={hasInvalidHTS ? 'Invalid HTS/Country Combination' : ''}
          >
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.country_of_origin &&
                transaction?.country_of_origin !== '' &&
                !hasInvalidHTS
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {transaction?.country_of_origin}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div className='screening-results-row-default-cell-text'>
              ${transaction?.tariff_result?.math?.totalCost.toFixed(2) || 0}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.tariff_result?.base?.baseDutyRate &&
                (transaction?.tariff_result?.base?.baseDutyRate === 'Free' ||
                  transaction?.tariff_result?.base?.baseDutyRate === '0%')
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {transaction?.tariff_result?.base?.baseDutyRate || '-'}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                additionalDuties() === '0.00%'
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {additionalDuties()}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.compliance_score &&
                transaction?.compliance_score < 60
                  ? { color: 'var(--low-color)' }
                  : transaction?.compliance_score &&
                    transaction?.compliance_score < 90
                  ? { color: 'var(--mid-color)' }
                  : {}
              }
            >
              {aiInProgress && (
                <span style={{ color: 'var(--mid-color)', fontSize: '12px' }}>
                  Processing...
                </span>
              )}
              {transaction?.compliance_score || 'No Assessment'}
            </div>
          </div>
        </div>
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: '100px', paddingLeft: '0px' }}
        >
          <div
            className='screening-results-row-default-cell-inner'
            style={{ width: '100px', paddingLeft: '0px' }}
          >
            {/*handleClassify && (
                <FactCheckIcon
                  onClick={() => handleClassify(transaction ? transaction : [])}
                  sx={{ fontSize: 20, color: 'var(--text-primary)' }}
                />
              )*/}

            {handleAdd && (
              <svg
                className='MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-dki1ri'
                style={{ fontSize: 20, color: 'var(--primary-text-color)' }}
                height={20}
                width={20}
                focusable='false'
                aria-hidden='true'
                viewBox='0 0 24 24'
                data-testid='AddOutlinedIcon'
                onClick={() => handleAdd(transaction ? transaction : [])}
                fill='var(--primary-text-color)'
              >
                <path
                  d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z'
                  stroke='var(--text-primary)'
                />
              </svg>
            )}

            <EditOutlinedIcon
              onClick={() => handleEdit(transaction ? transaction : [])}
              sx={{ fontSize: 20, color: 'var(--text-primary)' }}
            />
            <svg
              className='MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-dki1ri'
              style={{ fontSize: 20, color: 'var(--primary-text-color)' }}
              xmlns='http://www.w3.org/2000/svg'
              height={20}
              width={20}
              focusable='false'
              aria-hidden='true'
              viewBox='0 0 24 24'
              onClick={() => handleDelete(transaction ? transaction : [])}
            >
              <path
                d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                stroke='var(--text-primary)'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>

      <ChildTable />
    </>
  );
};

export default ProductPageRow;
